(() => {
  const q = (selector, context = document) => context.querySelector(selector);
  const qa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = matchMedia('(min-width: 960px)');

  qa('[data-collections-story]').forEach((story) => {
    const horizontal = q('[data-collections-horizontal]', story);
    const viewport = q('[data-collections-viewport]', story);
    const track = q('[data-collections-track]', story);
    const progressRail = q('[data-collections-progress]', story);
    const progressThumb = q('[data-collections-progress-thumb]', story);
    const title = q('[data-collections-title]', story);
    const titleLines = qa('[data-collections-title-line]', story);
    const badge = q('.wr-collections-cue__badge', story);
    const copy = q('.wr-collections-horizontal__copy', story);
    if (!horizontal || !viewport || !track) return;
    let scrollProgress = 0;
    let frame = 0;
    let readyTimer = 0;

    const fitTitle = () => {
      if (!title || !titleLines.length) return;
      const availableWidth = title.clientWidth;
      if (!availableWidth) return;
      titleLines.forEach((line) => {
        const naturalWidth = line.offsetWidth;
        if (!naturalWidth) return;
        const currentSize = parseFloat(getComputedStyle(line).fontSize);
        const fittedSize = currentSize * availableWidth / naturalWidth;
        if (Math.abs(fittedSize - currentSize) > .02) line.style.fontSize = fittedSize.toFixed(3) + 'px';
      });
      if (badge) {
        const style = getComputedStyle(badge);
        const innerWidth = badge.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight) - 8;
        const longestLine = Math.max(...qa('span', badge).map(line => line.offsetWidth));
        if (innerWidth > 0 && longestLine > 0) {
          const currentSize = parseFloat(style.fontSize);
          const fittedSize = currentSize * innerWidth / longestLine;
          if (Math.abs(fittedSize - currentSize) > .02) badge.style.fontSize = fittedSize.toFixed(3) + 'px';
        }
      }
    };

    const render = () => {
      const travel = Math.max(0, track.scrollWidth - viewport.clientWidth);
      const scrollDistance = Math.max(innerHeight * (desktop.matches ? 1.1 : .9), travel * (desktop.matches ? 1 : .92));
      const desiredHeight = innerHeight + scrollDistance;
      if (Math.abs(horizontal.offsetHeight - desiredHeight) > 2) horizontal.style.height = desiredHeight + 'px';
      const rect = horizontal.getBoundingClientRect();
      const scrollRange = Math.max(1, horizontal.offsetHeight - innerHeight);
      scrollProgress = Math.max(0, Math.min(1, -rect.top / scrollRange));
      // Scrub the copy exit using the same distance as the cards, without a timer.
      const moved = scrollProgress * travel;
      const exitDistance = Math.max(1, (copy?.clientWidth || viewport.clientWidth) * 1.1);
      const exitProgress = Math.max(0, Math.min(1, moved / exitDistance));
      // Ease the ends spatially: stopping or reversing scroll never starts a timer.
      const copyExit = exitProgress * exitProgress * (3 - 2 * exitProgress);
      horizontal.style.setProperty('--wr-copy-opacity', (1 - copyExit).toFixed(4));
      horizontal.style.setProperty('--wr-copy-offset', (reduced ? 0 : -48 * copyExit).toFixed(2) + 'px');
      if (copy) copy.inert = copyExit >= 1;
      horizontal.classList.toggle('is-scrolling', scrollProgress > .003);
      track.style.transform = 'translate3d(' + (-scrollProgress * travel) + 'px,0,0)';
      if (progressRail && progressThumb) {
        const collectionCount = Math.max(1, track.children.length);
        const thumbRatio = Math.max(.08, Math.min(1, 1 / collectionCount));
        const railWidth = progressRail.clientWidth;
        progressThumb.style.width = (thumbRatio * 100) + '%';
        progressThumb.style.transform = 'translate3d(' + (scrollProgress * railWidth * (1 - thumbRatio)) + 'px,0,0)';
      }
      frame = 0;
    };

    const requestRender = () => { if (!frame) frame = requestAnimationFrame(render); };
    const refreshLayout = () => {
      fitTitle();
      requestRender();
    };
    const revealStory = () => {
      window.clearTimeout(readyTimer);
      const entryDelay = document.documentElement.classList.contains('wr-page-enter') ? 900 : 180;
      readyTimer = window.setTimeout(() => story.classList.add('is-collection-ready'), reduced ? 0 : entryDelay);
    };
    fitTitle();
    render();
    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', refreshLayout, { passive: true });
    window.addEventListener('pageshow', refreshLayout, { passive: true });
    if (document.readyState === 'complete') revealStory();
    else window.addEventListener('load', revealStory, { once: true });
    document.fonts?.ready.then(refreshLayout);
    document.fonts?.addEventListener('loadingdone', refreshLayout);
    qa('img', track).forEach((image) => {
      if (!image.complete) image.addEventListener('load', requestRender, { once: true });
    });
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(refreshLayout);
      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
      if (title) resizeObserver.observe(title);
    }
  });

  if (!reduced && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('wr-scroll-motion-ready');
    const automaticTargets = qa('.wr-section-head,.wr-about__inner,.wr-lookbook__copy,.wr-collection-page__intro,.wr-collection-page__grid > .wr-product-card,.wr-product-app');
    automaticTargets.forEach((element, index) => {
      element.classList.add('wr-scroll-reveal');
      element.style.setProperty('--wr-reveal-delay', (index % 4) * 55 + 'ms');
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in-view');
        observer.unobserve(entry.target);
      });
    }, { threshold: .08, rootMargin: '0px 0px -8% 0px' });
    qa('.wr-scroll-reveal').forEach((element) => {
      if (element.getBoundingClientRect().top < innerHeight * .92) element.classList.add('is-in-view');
      else observer.observe(element);
    });
  }

  qa('[data-collection-banner]').forEach((bannerLink) => {
    bannerLink.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || bannerLink.target) return;
      bannerLink.closest('.wr-collections-banner')?.classList.add('is-activating');
      // Navigation remains owned by the global page transition so banners use
      // the same shrink-and-rise choreography as every other internal link.
    });
  });

  qa('[data-best-sellers-grid]').forEach(async (grid) => {
    const limit = Math.max(1, Number(grid.dataset.bestSellersLimit) || 8);
    const source = grid.dataset.bestSellersSource;
    const items = qa('[data-best-seller-product]', grid);
    if (!source || !items.length) return;

    const itemById = new Map(items.map((item) => [String(item.dataset.bestSellerProduct), item]));
    const randomize = (entries) => {
      const output = [...entries];
      for (let index = output.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
      }
      return output;
    };

    try {
      const response = await fetch(source, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const payload = await response.json();
      const ranked = (payload.products || [])
        .map((product) => itemById.get(String(product.id)))
        .filter(Boolean);
      const rankedSet = new Set(ranked);
      const fallback = randomize(items.filter((item) => !rankedSet.has(item)));
      const selected = [...ranked, ...fallback].slice(0, limit);
      if (!selected.length) return;

      items.forEach((item) => { item.hidden = true; });
      selected.forEach((item) => {
        item.hidden = false;
        grid.append(item);
      });
      grid.dataset.bestSellersReady = 'true';
    } catch (_) {
      // The Liquid-rendered first eight products remain as a resilient fallback.
    }
  });
})();
